import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { CheckCircle, Search, ShieldAlert, X, XCircle } from 'lucide-react'
import { Button, useToast } from '../components/radix'
import { AdminEnhancedPagination } from '../components/orders/AdminEnhancedPagination'
import { AdminOrderStatusPill } from '../components/orders/AdminOrderStatusPill'
import { AdminSelect } from '../components/orders/AdminSelect'
import { AdminSearchableSelect } from '../components/orders/AdminSearchableSelect'
import { OrdersTableSkeleton } from '../components/orders/OrdersTableSkeleton'
import { SupplierChangesTab } from '../components/orders/SupplierChangesTab'
import { AllOrdersTab } from '../components/orders/AllOrdersTab'
import { usePermission } from '../hooks/usePermission'
import { useMe } from '../auth/hooks/useMe'
import { useOrders } from '../auth/hooks/useOrders'
import { useSupplierChangesSummary } from '../auth/hooks/useSupplierChanges'
import type { OrderFilters } from '../auth/services/auth-api'
import { approveOrder, approveOrderWithoutSubmit, declineOrder, getOrdersStockStatus, getUsers, type OrderStockStatus } from '../auth/services/auth-api'
import { mapOrderForAdmin } from '../services/order-mappers'
import { APPROVAL_REASON_CONFIG, type ApprovalReasonType } from '../config/approval'
import { ORDER_STATUS_FILTER_OPTIONS } from '../constants/orderStatus'
import { SUPPLIER_CHANGES_TAB_PARAM } from '../constants/supplierChanges'
import { formatCurrency } from '../utils/currency'
import { splitDateTime } from '../utils/datetime'
import { parseApiError } from '../auth/utils/error-parser'
import { getAppEnv } from '../lib/env'

type OrderTab = 'my_team' | 'requires_approval' | 'supplier_changes' | 'all_orders'
type MappedOrder = ReturnType<typeof mapOrderForAdmin>

// The capped total a partial pending order would actually place at current
// stock \u2014 so the list matches the detail page (which caps to live stock)
// instead of showing the full requested total_amount. Null unless the order is
// awaiting approval with reduced/partial live stock and a total was computed.
function reducedTotal(
  order: { status: string; total: number },
  status: OrderStockStatus | undefined,
): number | null {
  if (order.status !== 'awaiting_approval' || !status || status.available_total == null) {
    return null
  }
  if (status.stock_status !== 'partial' && status.stock_status !== 'stock_reduced') {
    return null
  }
  const parsed = parseFloat(status.available_total)
  // Only surface it when it's an actual reduction from the requested total.
  if (Number.isNaN(parsed) || parsed >= order.total) return null
  return parsed
}

// A link can open this page on a single day, e.g. the "View your team's
// orders" button in the manager's daily digest email: ?date=YYYY-MM-DD. The
// link always names the exact day so it stays correct whenever it is opened;
// on arrival it is shown as "Today" or "Yesterday" when it is one of those,
// otherwise as its own `day:YYYY-MM-DD` option. Single days are sent to the
// server as both date_from and date_to.
const LINK_DATE_PARAM = 'date'
const DAY_FILTER_PREFIX = 'day:'
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
// Days follow UK time, matching the daily digest and the server's date filter.
const UK_TIME_ZONE = 'Europe/London'

// Today's date in the UK as YYYY-MM-DD, whatever the browser's timezone.
function ukToday(nowMs: number) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(nowMs))
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

// Calendar arithmetic on a YYYY-MM-DD day, safe across clock changes.
function shiftDay(day: string, days: number) {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date + days)).toISOString().slice(0, 10)
}

function dayFilterFromLink(params: URLSearchParams, nowMs: number): string | null {
  const day = params.get(LINK_DATE_PARAM)
  if (!day || !ISO_DAY_PATTERN.test(day)) return null
  if (Number.isNaN(new Date(`${day}T12:00:00Z`).getTime())) return null
  const today = ukToday(nowMs)
  if (day === today) return 'today'
  if (day === shiftDay(today, -1)) return 'yesterday'
  return `${DAY_FILTER_PREFIX}${day}`
}

// The single UK day a filter selects, or null for the multi-day ranges.
function singleDayForFilter(filter: string, nowMs: number): string | null {
  if (filter === 'today') return ukToday(nowMs)
  if (filter === 'yesterday') return shiftDay(ukToday(nowMs), -1)
  return dayFromFilter(filter)
}

function dayFromFilter(filter: string): string | null {
  return filter.startsWith(DAY_FILTER_PREFIX) ? filter.slice(DAY_FILTER_PREFIX.length) : null
}

// "Fri 2 Oct 2026" — read at midday UTC so no timezone can shift the day.
function formatDayLabel(day: string) {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  })
}

// Convert the date-range dropdown value into a YYYY-MM-DD `date_from` param
// (applied server-side as created_at__date__gte).
function dateFromParam(filter: string, nowMs: number) {
  const days = filter === '7days' ? 7 : filter === '90days' ? 90 : 30
  const from = new Date(nowMs - days * 24 * 60 * 60 * 1000)
  const month = String(from.getMonth() + 1).padStart(2, '0')
  const day = String(from.getDate()).padStart(2, '0')
  return `${from.getFullYear()}-${month}-${day}`
}

// Relative "time since submission" (APR-003 AC1). `nowMs` is captured once per
// mount and passed in to keep this pure (no Date.now() during render).
function timeSince(dateString: string, nowMs: number) {
  const mins = Math.floor((nowMs - new Date(dateString).getTime()) / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

// Display name for a user (falls back to email).
function userLabel(user: { first_name: string; last_name: string; email: string }) {
  return [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email
}

export function Orders() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const { can } = usePermission()
  const { data: me } = useMe()

  // Approve/decline actions require the approve_orders permission (approver /
  // admin). Non-approvers still see both tabs and the queue — just read-only.
  const canApprove = can('approve_orders')

  const [searchParams] = useSearchParams()
  // ?tab=supplier-changes opens that tab, e.g. coming back from an order's
  // supplier changes page.
  const [activeTab, setActiveTab] = useState<OrderTab>(() =>
    searchParams.get('tab') === SUPPLIER_CHANGES_TAB_PARAM ? 'supplier_changes' : 'my_team',
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [engineerFilter, setEngineerFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState(
    () => dayFilterFromLink(searchParams, Date.now()) ?? '30days',
  )
  // Approver filter on the Requires Approval tab. Empty = fall back to the
  // current user (default view = my assignments); 'all' = shared queue across
  // all approvers; otherwise a specific approver id (APR-015 AC2/AC3).
  const [approverFilter, setApproverFilter] = useState('')
  // Approval-reason filter (Requires Approval tab only, APR-015 AC3). 'all' =
  // no filter; any other value is one of ApprovalReasonType and matched
  // server-side against the JSONField approval_reasons list.
  const [approvalReasonFilter, setApprovalReasonFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set())
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showDeclineDialog, setShowDeclineDialog] = useState(false)
  const [approveNote, setApproveNote] = useState('')
  const [declineReason, setDeclineReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nowMs] = useState(() => Date.now())

  const isApprovalTab = activeTab === 'requires_approval'
  // Supplier changes (PO-008): who sees it is decided by the server.
  const canViewSupplierChanges = me?.can_view_supplier_changes === true
  // SaaS admins have no team or approvals of their own, so they see only the
  // Supplier changes tab (every company's orders).
  const isSaasAdmin = me?.is_saas_admin === true
  // A SaaS admin's tabs are Supplier changes (where they land) and All orders.
  const saasTab = activeTab === 'all_orders' ? 'all_orders' : 'supplier_changes'
  const isAllOrdersTab = isSaasAdmin && saasTab === 'all_orders'
  const isSupplierChangesTab = (isSaasAdmin ? saasTab === 'supplier_changes' : activeTab === 'supplier_changes') && canViewSupplierChanges
  // The tab's count of orders with changes, shown on every tab (as in the mock).
  const { data: supplierChangesSummary } = useSupplierChangesSummary('', canViewSupplierChanges)
  const supplierChangesCount = supplierChangesSummary?.count

  // Debounce the search box so typing fires one request, not one per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      // Reset page + clear any selection, like the other filter changes do —
      // otherwise the bulk bar could still act on now-hidden orders.
      setCurrentPage(1)
      setSelectedOrderIds(new Set())
    }, 400)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Effective approver selection: default to the current user until they pick
  // a specific approver or "All approvers".
  const effectiveApprover = approverFilter !== '' ? approverFilter : me?.id ?? 'all'

  // All filters are applied server-side (OrderViewSet.get_queryset).
  const filters: OrderFilters = useMemo(() => {
    const next: OrderFilters = {}
    const singleDay = singleDayForFilter(dateFilter, nowMs)
    if (singleDay) {
      next.dateFrom = singleDay
      next.dateTo = singleDay
    } else if (dateFilter !== 'all') {
      next.dateFrom = dateFromParam(dateFilter, nowMs)
    }
    if (isApprovalTab) {
      // Requires Approval — awaiting_approval across the org, oldest first.
      // Approver dropdown drives who we see: a specific approver id (default =
      // current user → my assignments) or 'all' (shared queue).
      next.status = 'awaiting_approval'
      next.sort = 'oldest'
      if (effectiveApprover !== 'all') next.approver = effectiveApprover
      if (approvalReasonFilter !== 'all') next.approvalReason = approvalReasonFilter
    } else {
      // My Team Orders — orders from engineers who report to me, all statuses.
      next.team = 'me'
      if (statusFilter !== 'all') next.status = statusFilter
    }
    if (engineerFilter !== 'all') next.engineer = engineerFilter
    return next
  }, [isApprovalTab, statusFilter, engineerFilter, effectiveApprover, approvalReasonFilter, dateFilter, nowMs])

  const { data, isLoading, error } = useOrders(
    debouncedSearch || undefined,
    currentPage,
    false,
    itemsPerPage,
    filters,
  )

  const orders: MappedOrder[] = useMemo(() => (data?.results || []).map(mapOrderForAdmin), [data])

  // Filter dropdown options come from the tenant's users (id → name), so they
  // stay complete under pagination (not derived from the current page).
  const { data: usersData } = useQuery({
    queryKey: ['order-filter-users'],
    queryFn: () => getUsers(500),
    staleTime: 5 * 60 * 1000,
  })
  // Engineer dropdown: base-role members only.
  const engineerOptions = useMemo(
    () =>
      (usersData?.results ?? [])
        .filter((orgUser) => orgUser.base_role === 'member')
        .map((orgUser) => ({ value: orgUser.id, label: userLabel(orgUser) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [usersData],
  )

  // Approver dropdown: base-role approvers, plus the current user (so the
  // default selection resolves even if they aren't a base approver).
  const approverOptions = useMemo(() => {
    const options = (usersData?.results ?? [])
      .filter((orgUser) => orgUser.base_role === 'approver')
      .map((orgUser) => ({ value: orgUser.id, label: userLabel(orgUser) }))
    if (me?.id && !options.some((option) => option.value === me.id)) {
      options.push({ value: me.id, label: userLabel(me) })
    }
    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [usersData, me])

  const totalItems = data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const monthlyTotal = parseFloat(data?.total_amount ?? '0')
  const paginatedOrders = orders
  const pageIds = paginatedOrders.map((order) => order.id)
  const somePageSelected = pageIds.some((id) => selectedOrderIds.has(id))
  const selectedCount = selectedOrderIds.size

  // Live stock status for the visible rows on the Requires Approval tab. Fetched
  // separately from the list so the table renders instantly and the badges fill
  // in; cached server-side (3-min TTL) + here (staleTime) so it doesn't re-hit
  // the supplier on every load. Only the visible page's IDs are requested.
  const stockStatusKey = pageIds.slice().sort().join(',')
  // On a fresh page load / hard refresh, bypass the server cache for live data;
  // subsequent auto-refetches (poll / focus) use the short-lived cache.
  const forceFirstFetch = useRef(true)
  const { data: stockStatusMap } = useQuery({
    queryKey: ['orders-stock-status', stockStatusKey],
    queryFn: () => {
      const force = forceFirstFetch.current
      forceFirstFetch.current = false
      return getOrdersStockStatus(pageIds, force)
    },
    enabled: isApprovalTab && canApprove && pageIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    // Keep badges current while the manager watches the queue (only polls while
    // the tab is visible; correctness still comes from the approve-time recheck).
    refetchInterval: isApprovalTab ? 60 * 1000 : false,
  })
  // Accumulate statuses across pages. The query only fetches the visible page,
  // but a selection can span pages — so the skip / decline-only logic needs the
  // status of every *selected* order, not just the current page's. Without this,
  // an off-page all-out-of-stock order fails the all_out_of_stock check (no data)
  // and gets wrongly approved. Fresh results overwrite old, so visible badges
  // still reflect the latest fetch.
  const [stockStatuses, setStockStatuses] = useState<Record<string, OrderStockStatus>>({})
  useEffect(() => {
    if (stockStatusMap) {
      setStockStatuses((previous) => ({ ...previous, ...stockStatusMap }))
    }
  }, [stockStatusMap])

  // An order is "checking" until its live status arrives (or came back unknown).
  // Only relevant on the approval tab; elsewhere nothing is "checking".
  const isChecking = (id: string) => {
    if (!isApprovalTab) return false
    const status = stockStatuses[id]
    return !status || status.stock_status === 'unknown'
  }
  const isAllOutOfStock = (id: string) => stockStatuses[id]?.all_out_of_stock === true

  // Only computed (non-checking) rows are selectable; select-all acts on those.
  const selectablePageIds = pageIds.filter((id) => !isChecking(id))
  const allSelectableSelected =
    selectablePageIds.length > 0 && selectablePageIds.every((id) => selectedOrderIds.has(id))

  // Split the current selection for the smart bulk-approve flow: fully
  // out-of-stock orders can't be approved (backend refuses), only declined.
  const selectedIds = Array.from(selectedOrderIds)
  const outOfStockSelected = selectedIds.filter(isAllOutOfStock)
  // Approvable = not fully out of stock AND its status is currently known. A
  // refetch can flip a selected order back to "checking"; excluding it here
  // keeps the checking-state guard honest (never approve an order whose live
  // status just failed to compute — it can be re-approved once it resolves).
  const approvableSelected = selectedIds.filter((id) => !isAllOutOfStock(id) && !isChecking(id))

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    statusFilter !== 'all' ||
    engineerFilter !== 'all' ||
    (isApprovalTab && effectiveApprover !== (me?.id ?? 'all')) ||
    (isApprovalTab && approvalReasonFilter !== 'all') ||
    dateFilter !== defaultDateForTab(activeTab)

  const resetPage = () => {
    setCurrentPage(1)
    setSelectedOrderIds(new Set())
  }

  const clearFilters = (tab: OrderTab = activeTab) => {
    setSearchQuery('')
    setStatusFilter('all')
    setEngineerFilter('all')
    setApproverFilter('')
    setApprovalReasonFilter('all')
    setDateFilter(defaultDateForTab(tab))
    resetPage()
  }

  const handleTabChange = (tab: OrderTab) => {
    setActiveTab(tab)
    clearFilters(tab)
  }

  const toggleOrderSelection = (orderId: string) => {
    // Can't act on an order whose stock status is still being checked.
    if (isChecking(orderId)) return
    setSelectedOrderIds((previous) => {
      const next = new Set(previous)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  const toggleSelectAll = () => {
    // Select-all only touches rows with a known status (skip "checking").
    setSelectedOrderIds((previous) => {
      const next = new Set(previous)
      if (allSelectableSelected) selectablePageIds.forEach((id) => next.delete(id))
      else selectablePageIds.forEach((id) => next.add(id))
      return next
    })
  }

  // Shared bulk runner: acts on every selected order independently so a single
  // failure (e.g. an order already acted on → 409) doesn't sink the whole
  // batch. Reports how many succeeded/failed and keeps the failed ones
  // selected so they can be retried.
  const runBulkAction = async (
    ids: string[],
    action: (id: string) => Promise<unknown>,
    pastTense: string,
    presentTense: string,
    closeDialog: () => void,
    skipped = 0,
  ) => {
    const plural = (count: number) => (count === 1 ? '' : 's')
    if (ids.length === 0) {
      closeDialog()
      if (skipped > 0) {
        addToast(`All ${skipped} selected order${plural(skipped)} are out of stock — decline them instead`, 'warning')
      }
      return
    }
    setIsSubmitting(true)
    const results = await Promise.allSettled(ids.map((id) => action(id)))
    setIsSubmitting(false)
    closeDialog()

    const failedIds = ids.filter((_, index) => results[index].status === 'rejected')
    const succeeded = ids.length - failedIds.length
    const skipMsg = skipped > 0 ? ` · skipped ${skipped} out of stock` : ''

    if (failedIds.length === 0) {
      addToast(`${pastTense} ${succeeded} order${plural(succeeded)}${skipMsg}`, 'success')
      setSelectedOrderIds(new Set())
      setCurrentPage(1)
    } else if (succeeded === 0) {
      const firstError = results.find((result) => result.status === 'rejected')
      const parsed = firstError ? parseApiError((firstError as PromiseRejectedResult).reason) : null
      addToast(parsed?.message || `Failed to ${presentTense} ${failedIds.length} order${plural(failedIds.length)}`, 'error')
    } else {
      addToast(`${pastTense} ${succeeded}; ${failedIds.length} failed${skipMsg}`, 'warning')
      setSelectedOrderIds(new Set(failedIds))
      setCurrentPage(1)
    }
    await queryClient.invalidateQueries({ queryKey: ['orders'] })
  }

  // Pre-prod only: offer approving WITHOUT sending the orders to the supplier,
  // so bulk approval can be exercised without placing real supplier orders.
  // Every other environment keeps the single approve-and-submit button.
  const isPreProd = getAppEnv() === 'pre-prod'

  const handleBulkApprove = (submitToSupplier = true) =>
    // Skip fully out-of-stock orders — the backend refuses them; they must be
    // declined. Approve only the placeable ones and report how many were skipped.
    runBulkAction(
      approvableSelected,
      (id) => {
        const payload = { notes: approveNote.trim() || undefined }
        return submitToSupplier
          ? approveOrder(id, payload)
          : approveOrderWithoutSubmit(id, payload)
      },
      submitToSupplier ? 'Approved' : 'Approved (not sent)',
      'approve',
      () => { setShowApproveDialog(false); setApproveNote('') },
      outOfStockSelected.length,
    )

  const handleBulkDecline = () => {
    if (!declineReason.trim()) return
    // Decline acts on the full selection (any order can be declined).
    return runBulkAction(
      selectedIds,
      (id) => declineOrder(id, { reason: declineReason.trim() }),
      'Declined',
      'decline',
      () => { setShowDeclineDialog(false); setDeclineReason('') },
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6">
        <h3 className="mb-2 font-bold text-red-900">Failed to load orders</h3>
        <p className="text-sm text-red-700">{error.message || 'Please try again later'}</p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>Retry</Button>
      </div>
    )
  }

  const filterSelectClass = ''

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Orders</h1>
          {!isSupplierChangesTab && !isAllOrdersTab && <p className="text-xs text-gray-400 sm:text-sm">{isLoading ? 'Loading…' : `${totalItems} orders`}</p>}
        </div>
        {isSupplierChangesTab || isAllOrdersTab ? null : isLoading ? (
          <span className="inline-block h-7 w-24 animate-pulse rounded bg-gray-200" />
        ) : (
          <span className="text-lg font-bold tabular-nums text-gray-900 sm:text-2xl">{formatCurrency(monthlyTotal, 2)}</span>
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-0 overflow-x-auto">
          {!isSaasAdmin && <button type="button" onClick={() => handleTabChange('my_team')} className={`relative whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'my_team' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>My team orders</button>}
          {!isSaasAdmin && <button type="button" onClick={() => handleTabChange('requires_approval')} className={`relative whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${isApprovalTab ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>Requires approval</button>}
          {canViewSupplierChanges && <button type="button" onClick={() => handleTabChange('supplier_changes')} className={`relative inline-flex items-center whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${isSupplierChangesTab ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>Supplier changes to review{supplierChangesCount !== undefined && <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${isSupplierChangesTab ? 'bg-brand-primary text-[var(--brand-primary-text)]' : 'bg-gray-100 text-gray-700'}`}>{supplierChangesCount}<span className="sr-only"> order{supplierChangesCount === 1 ? '' : 's'}</span></span>}</button>}
          {isSaasAdmin && <button type="button" onClick={() => handleTabChange('all_orders')} className={`relative whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${isAllOrdersTab ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>All orders</button>}
        </nav>
      </div>

      {isAllOrdersTab ? <AllOrdersTab /> : isSupplierChangesTab ? <SupplierChangesTab onShowAllOrders={() => handleTabChange(isSaasAdmin ? 'all_orders' : 'my_team')} /> : (<>

      {isApprovalTab && canApprove && selectedCount > 0 && (
        <div className="hidden lg:block">
          <div className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2">
            <span className="text-sm font-medium text-gray-700">{selectedCount} selected</span>
            {outOfStockSelected.length > 0 && <span className="text-xs font-medium text-red-600">· {outOfStockSelected.length} out of stock (decline only)</span>}
            <div className="ml-auto flex gap-2">
              {approvableSelected.length > 0 && <button type="button" onClick={() => { setApproveNote(''); setShowApproveDialog(true) }} className="inline-flex items-center rounded-full bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700"><CheckCircle className="mr-1.5 h-3.5 w-3.5" />Approve</button>}
              <button type="button" onClick={() => { setDeclineReason(''); setShowDeclineDialog(true) }} className="inline-flex items-center rounded-full bg-red-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-600"><XCircle className="mr-1.5 h-3.5 w-3.5" />Decline</button>
              <button type="button" onClick={() => setSelectedOrderIds(new Set())} className="inline-flex items-center rounded-lg px-2 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700">Clear</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 lg:hidden">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search orders..." value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {!isApprovalTab && <StatusFilter value={statusFilter} onChange={(value) => { setStatusFilter(value); resetPage() }} className={filterSelectClass} />}
          <NamedFilter value={engineerFilter} onChange={(value) => { setEngineerFilter(value); resetPage() }} className={filterSelectClass} label="All engineers" options={engineerOptions} />
          {isApprovalTab && <NamedFilter value={effectiveApprover} onChange={(value) => { setApproverFilter(value); resetPage() }} className={filterSelectClass} label="All approvers" options={approverOptions} />}
          <DateFilter value={dateFilter} onChange={(value) => { setDateFilter(value); resetPage() }} className={filterSelectClass} />
          {isApprovalTab && <ApprovalReasonFilter value={approvalReasonFilter} onChange={(value) => { setApprovalReasonFilter(value); resetPage() }} className={filterSelectClass} />}
        </div>
      </div>

      <div className="hidden items-center gap-2 lg:flex">
        <div className="relative min-w-0 flex-[2]"><Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" /><input type="text" placeholder="Search orders..." value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary" /></div>
        {!isApprovalTab && <StatusFilter value={statusFilter} onChange={(value) => { setStatusFilter(value); resetPage() }} className={`${filterSelectClass} min-w-0 flex-1`} />}
        <NamedFilter value={engineerFilter} onChange={(value) => { setEngineerFilter(value); resetPage() }} className={`${filterSelectClass} min-w-0 flex-1`} label="All engineers" options={engineerOptions} />
        {isApprovalTab && <NamedFilter value={effectiveApprover} onChange={(value) => { setApproverFilter(value); resetPage() }} className={`${filterSelectClass} min-w-0 flex-1`} label="All approvers" options={approverOptions} />}
        <DateFilter value={dateFilter} onChange={(value) => { setDateFilter(value); resetPage() }} className={`${filterSelectClass} min-w-0 flex-1`} />
        {isApprovalTab && <ApprovalReasonFilter value={approvalReasonFilter} onChange={(value) => { setApprovalReasonFilter(value); resetPage() }} className={`${filterSelectClass} min-w-0 flex-1`} />}
      </div>

      {isLoading ? (
        <OrdersTableSkeleton />
      ) : orders.length === 0 ? (
        <div className="rounded-[16px] border-2 border-gray-200 bg-white p-12 text-center">
          {hasActiveFilters ? (
            <>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">No orders match your filters</h3>
              <p className="mb-4 text-gray-500">Try adjusting your search or filters.</p>
              <Button variant="outline" onClick={clearFilters}>Clear filters</Button>
            </>
          ) : (
            <>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">No orders found</h3>
              <p className="text-gray-500">{isApprovalTab ? 'No orders require approval' : 'No orders yet'}</p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-[16px] border-2 border-gray-200 bg-white lg:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 bg-brand-primary">{isApprovalTab && canApprove && <th className="w-10 p-3"><input type="checkbox" checked={allSelectableSelected} ref={(element) => { if (element) element.indeterminate = somePageSelected && !allSelectableSelected }} onChange={toggleSelectAll} disabled={selectablePageIds.length === 0} className="h-4 w-4 cursor-pointer rounded border-gray-300 text-brand-primary focus:ring-brand-primary disabled:cursor-not-allowed disabled:opacity-40" /></th>}<th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Order #</th><th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Date</th><th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Engineer</th><th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Supplier</th>{isApprovalTab && <th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Reason</th>}{isApprovalTab && canApprove && <th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Stock</th>}{isApprovalTab && <th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Approver</th>}{!isApprovalTab && <th className="p-3 text-left font-semibold text-[var(--brand-primary-text)]">Status</th>}<th className="p-3 text-right font-semibold text-[var(--brand-primary-text)]">Total</th></tr></thead>
                <tbody>
                  {paginatedOrders.map((order) => {
                    const isSelected = selectedOrderIds.has(order.id)
                    const { date, time } = splitDateTime(order.placed_at)
                    return (
                      <tr key={order.id} onClick={() => navigate(`/${tenantSlug}/orders/${order.id}`)} className={`cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50 ${isSelected ? 'bg-brand-primary-lightest' : ''}`}>
                        {isApprovalTab && canApprove && <td className="w-10 p-3" onClick={(event) => event.stopPropagation()}><input type="checkbox" checked={isSelected} disabled={isChecking(order.id)} onChange={() => toggleOrderSelection(order.id)} className="h-4 w-4 cursor-pointer rounded border-gray-300 text-brand-primary focus:ring-brand-primary disabled:cursor-not-allowed disabled:opacity-40" /></td>}
                        <td className="p-3"><div className="font-medium text-gray-900">{order.order_number}</div><div className="text-xs text-gray-400">{order.transaction_id}</div></td>
                        <td className="p-3"><div className="text-gray-900">{date}</div><div className="text-xs text-gray-400">{isApprovalTab ? timeSince(order.placed_at, nowMs) : time}</div></td>
                        <td className="p-3"><div className="text-gray-900">{order.placed_by}</div>{order.business_area && <div className="text-xs text-gray-400">{order.business_area}</div>}</td>
                        <td className="p-3 text-gray-600">{order.supplier_name}</td>
                        {isApprovalTab && <td className="p-3"><ReasonPills reasons={order.approval_reasons} /><OrderReasonLines reason={order.reason} secondaryReason={order.secondary_reason} /></td>}
                        {isApprovalTab && canApprove && <td className="p-3"><StockStatusBadge status={stockStatuses[order.id]} /></td>}
                        {isApprovalTab && <td className="p-3 text-gray-700">{order.assigned_approver || <span className="italic text-gray-400">Unassigned</span>}</td>}
                        {!isApprovalTab && <td className="p-3"><AdminOrderStatusPill status={order.status} /></td>}
                        <td className="p-3 text-right font-semibold text-gray-900 tabular-nums">{(() => { const capped = reducedTotal(order, stockStatuses[order.id]); return formatCurrency(capped ?? order.total, 2) })()}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-3 lg:hidden">
            {paginatedOrders.map((order) => {
              const isSelected = selectedOrderIds.has(order.id)
              const { date, time } = splitDateTime(order.placed_at)
              return (
                <div key={order.id} onClick={() => navigate(`/${tenantSlug}/orders/${order.id}`)} className={`cursor-pointer overflow-hidden rounded-2xl border bg-white shadow-sm transition-shadow hover:shadow-md ${isSelected ? 'border-brand-primary bg-brand-primary-lightest' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2"><div className="flex min-w-0 items-center gap-2">{isApprovalTab && canApprove && <input type="checkbox" checked={isSelected} disabled={isChecking(order.id)} onChange={() => toggleOrderSelection(order.id)} onClick={(event) => event.stopPropagation()} className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 text-brand-primary focus:ring-brand-primary disabled:cursor-not-allowed disabled:opacity-40" />}<span className="truncate text-sm font-bold text-gray-900">{order.placed_by}</span>{order.business_area && <span className="text-xs text-gray-400">{order.business_area}</span>}</div>{isApprovalTab ? (canApprove ? <StockStatusBadge status={stockStatuses[order.id]} /> : null) : <AdminOrderStatusPill status={order.status} />}</div>
                  <div className="px-3 py-2.5"><div className="mb-1.5 flex items-center justify-between"><span className="text-sm font-bold text-gray-900">#{order.order_number}</span>{(() => { const capped = reducedTotal(order, stockStatuses[order.id]); return <span className="text-base font-bold tabular-nums text-gray-900">{formatCurrency(capped ?? order.total)}</span> })()}</div><div className="mb-1 flex items-center gap-2 text-xs text-gray-400"><span>{date}{time ? `, ${time}` : ''}</span>{isApprovalTab && <span className="text-gray-300">·</span>}{isApprovalTab && <span>{timeSince(order.placed_at, nowMs)}</span>}</div><p className="mb-2 text-xs text-gray-500">{order.supplier_name}</p>{isApprovalTab && (order.approval_reasons.length > 0 || order.reason || order.secondary_reason) && <div className="mb-2">{order.approval_reasons.length > 0 && <ReasonPills reasons={order.approval_reasons} compact />}<OrderReasonLines reason={order.reason} secondaryReason={order.secondary_reason} compact /></div>}{isApprovalTab && order.assigned_approver && <p className="text-xs text-gray-500">Approver: <span className="font-medium text-gray-700">{order.assigned_approver}</span></p>}</div>
                </div>
              )
            })}
          </div>

          <AdminEnhancedPagination currentPage={Math.min(currentPage, Math.max(totalPages, 1))} totalPages={totalPages} itemsPerPage={itemsPerPage} totalItems={totalItems} onPageChange={setCurrentPage} onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1) }} />
        </>
      )}
      </>)}

      {isApprovalTab && canApprove && selectedCount > 0 && <BulkMobileBar selectedCount={selectedCount} approvableCount={approvableSelected.length} outOfStockCount={outOfStockSelected.length} onApprove={() => { setApproveNote(''); setShowApproveDialog(true) }} onDecline={() => { setDeclineReason(''); setShowDeclineDialog(true) }} onClear={() => setSelectedOrderIds(new Set())} />}

      <ConfirmDialog open={showApproveDialog} onOpenChange={setShowApproveDialog} title={`Approve ${approvableSelected.length} order${approvableSelected.length === 1 ? '' : 's'}`} description={outOfStockSelected.length > 0 ? `${outOfStockSelected.length} selected order${outOfStockSelected.length === 1 ? ' is' : 's are'} out of stock and will NOT be approved — decline ${outOfStockSelected.length === 1 ? 'it' : 'them'} separately. Only the ${approvableSelected.length} available order${approvableSelected.length === 1 ? '' : 's'} will be approved.` : `Add an optional note for the engineer${approvableSelected.length === 1 ? '' : 's'}.`} value={approveNote} onValueChange={setApproveNote} placeholder="Optional note, e.g. Approved - collect before end of day..." rows={2} confirmLabel={isPreProd ? 'Approve & send to supplier' : 'Confirm approve'} confirmClassName="bg-green-600 hover:bg-green-700" disabled={isSubmitting} onConfirm={() => void handleBulkApprove(true)} notice={isPreProd ? `This environment is connected to live supplier ordering. Sending will place ${approvableSelected.length} real supplier order${approvableSelected.length === 1 ? '' : 's'}. "Approve only" records the approvals without sending anything.` : undefined} secondaryLabel={isPreProd ? 'Approve only' : undefined} onSecondary={isPreProd ? () => void handleBulkApprove(false) : undefined} />
      <ConfirmDialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog} title={`Decline ${selectedCount} order${selectedCount === 1 ? '' : 's'}`} description={`Reason will be shared with the engineer${selectedCount === 1 ? '' : 's'}.`} value={declineReason} onValueChange={setDeclineReason} placeholder="e.g. Budget exceeded, use alternative product..." rows={3} confirmLabel="Confirm decline" confirmClassName="bg-red-600 hover:bg-red-700" disabled={isSubmitting || declineReason.trim() === ''} onConfirm={handleBulkDecline} />
    </div>
  )
}

const DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7days', label: 'Last 7 days' },
  { value: '30days', label: 'Last 30 days' },
  { value: '90days', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
]

// Default date range per tab: the approval queue must not hide stuck approvals
// (>30 days old), so it defaults to All Time; the general list defaults to 30
// days (matches the mock).
function defaultDateForTab(tab: OrderTab) {
  return tab === 'requires_approval' ? 'all' : '30days'
}

// Live stock status display for the Requires Approval table (APR-009/010).
// All states render as plain colored text (consistent styling — no pills), so
// they read uniformly alongside the Reasons column; an unchanged order shows a
// quiet hyphen.
const STOCK_TEXT: Record<string, { label: string; cls: string }> = {
  partial: { label: 'Partial', cls: 'text-amber-700' },
  stock_reduced: { label: 'Stock reduced', cls: 'text-amber-700' },
  date_changed: { label: 'Date changed', cls: 'text-blue-700' },
}

function StockStatusBadge({ status }: { status?: OrderStockStatus }) {
  // No status yet → the live check is still in flight.
  if (!status) {
    return <span className="inline-flex items-center gap-1 text-xs text-gray-400"><span className="h-2 w-2 animate-pulse rounded-full bg-gray-300" />Checking…</span>
  }
  // Backend returned but couldn't determine stock (supplier fetch failed) → show
  // an error state instead of a perpetual "Checking…". The queue auto-retries on
  // the 60s poll; still non-selectable for bulk-approve (see isChecking).
  if (status.stock_status === 'unknown') {
    return <span className="whitespace-nowrap text-xs font-medium text-amber-700" title="Couldn't check live stock — retrying automatically.">Stock unavailable</span>
  }
  // Blocking state (decline-only, skipped by bulk-approve) → plain red text,
  // consistent with the other stock labels below.
  if (status.stock_status === 'out_of_stock') {
    return <span className="whitespace-nowrap text-xs font-medium text-red-700">Out of stock</span>
  }
  // Approvable-but-changed → plain colored text (no pill).
  const cfg = STOCK_TEXT[status.stock_status]
  if (cfg) {
    return <span className={`whitespace-nowrap text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>
  }
  // no_change (or any unmapped status) → quiet hyphen.
  return <span className="text-gray-400">-</span>
}

function StatusFilter({ value, onChange, className }: { value: string; onChange: (value: string) => void; className: string }) {
  return <div className={className}><AdminSelect size="sm" value={value} onChange={onChange} options={ORDER_STATUS_FILTER_OPTIONS} /></div>
}

function NamedFilter({ value, onChange, className, label, options }: { value: string; onChange: (value: string) => void; className: string; label: string; options: { value: string; label: string }[] }) {
  const selectOptions = [{ value: 'all', label }, ...options]
  return <div className={className}><AdminSearchableSelect size="sm" value={value} onChange={onChange} options={selectOptions} placeholder={label} searchPlaceholder="Search..." /></div>
}

function DateFilter({ value, onChange, className }: { value: string; onChange: (value: string) => void; className: string }) {
  // A day opened from a link shows as its own option until another range is picked.
  const day = dayFromFilter(value)
  const options = day ? [{ value, label: formatDayLabel(day) }, ...DATE_OPTIONS] : DATE_OPTIONS
  return <div className={className}><AdminSelect size="sm" value={value} onChange={onChange} options={options} /></div>
}

// Requires-Approval-tab-only filter (APR-015 AC3). Options mirror the six
// ApprovalReason enum values, labelled from APPROVAL_REASON_CONFIG so they
// stay in sync with the pills rendered in the row.
const APPROVAL_REASON_FILTER_OPTIONS = [
  { value: 'all', label: 'All approval reasons' },
  ...(Object.entries(APPROVAL_REASON_CONFIG) as [ApprovalReasonType, { label: string }][])
    .map(([value, { label }]) => ({ value, label })),
]

function ApprovalReasonFilter({ value, onChange, className }: { value: string; onChange: (value: string) => void; className: string }) {
  return <div className={className}><AdminSelect size="sm" value={value} onChange={onChange} options={APPROVAL_REASON_FILTER_OPTIONS} /></div>
}

function ReasonPills({ reasons, compact = false }: { reasons: string[]; compact?: boolean }) {
  if (reasons.length === 0) return <span className="text-gray-400">-</span>
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((reason) => {
        const config = APPROVAL_REASON_CONFIG[reason as ApprovalReasonType]
        return <span key={reason} className={`${compact ? 'rounded px-1.5 py-0.5 text-[10px]' : 'rounded-full px-2 py-1 text-xs'} font-medium ${config ? `${config.bg} ${config.text}` : 'bg-gray-100 text-gray-600'}`}>{config ? config.label : reason}</span>
      })}
    </div>
  )
}

// Engineer-provided order reason + cascading secondary reason. Rendered under
// the approval-trigger pills so approvers see both "why this needs approval"
// (system tags) and "why the engineer ordered it" (their stated reason).
function OrderReasonLines({ reason, secondaryReason, compact = false }: { reason?: string | null; secondaryReason?: string | null; compact?: boolean }) {
  if (!reason && !secondaryReason) return null
  const size = compact ? 'text-[10px]' : 'text-xs'
  return (
    <div className={`mt-1 space-y-0.5 ${size} text-gray-500`}>
      {reason && <div className="text-gray-700">{reason}</div>}
      {secondaryReason && <div>{secondaryReason}</div>}
    </div>
  )
}

function BulkMobileBar({ selectedCount, approvableCount, outOfStockCount, onApprove, onDecline, onClear }: { selectedCount: number; approvableCount: number; outOfStockCount: number; onApprove: () => void; onDecline: () => void; onClear: () => void }) {
  return (
    <div className="fixed bottom-[65px] left-0 right-0 z-50 bg-brand-primary lg:hidden">
      <div className="flex items-center gap-2 px-4 py-3">
        <span className="text-sm font-medium text-white">{selectedCount} selected{outOfStockCount > 0 ? ` · ${outOfStockCount} out of stock` : ''}</span>
        <div className="ml-auto flex gap-2">
          {approvableCount > 0 && <button type="button" onClick={onApprove} className="inline-flex items-center rounded-full bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700"><CheckCircle className="mr-1.5 h-3.5 w-3.5" />Approve</button>}
          <button type="button" onClick={onDecline} className="inline-flex items-center rounded-full bg-red-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-600"><XCircle className="mr-1.5 h-3.5 w-3.5" />Decline</button>
          <button type="button" onClick={onClear} className="inline-flex items-center rounded-lg px-2 py-1.5 text-xs font-medium text-gray-400 transition-colors hover:text-white">Clear</button>
        </div>
      </div>
    </div>
  )
}

function ConfirmDialog({ open, onOpenChange, title, description, value, onValueChange, placeholder, rows, confirmLabel, confirmClassName, disabled, onConfirm, notice, secondaryLabel, onSecondary }: { open: boolean; onOpenChange: (open: boolean) => void; title: string; description: string; value: string; onValueChange: (value: string) => void; placeholder: string; rows: number; confirmLabel: string; confirmClassName: string; disabled: boolean; onConfirm: () => void; notice?: string; secondaryLabel?: string; onSecondary?: () => void }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className={`fixed left-1/2 top-1/2 z-50 w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-4 shadow-xl sm:p-5 ${onSecondary ? 'max-w-lg' : 'max-w-md'}`}>
          <Dialog.Close asChild><button type="button" className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button></Dialog.Close>
          <div className="mb-3"><Dialog.Title className="text-lg font-bold text-gray-900">{title}</Dialog.Title><Dialog.Description className="mt-1 text-sm text-gray-500">{description}</Dialog.Description></div>
          {notice && (<div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"><ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" /><p className="text-xs leading-relaxed text-amber-900">{notice}</p></div>)}<textarea value={value} onChange={(event) => onValueChange(event.target.value)} placeholder={placeholder} rows={rows} className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary" />
          <div className={`mt-3 gap-2 ${onSecondary ? 'flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end' : 'flex justify-end'}`}><button type="button" onClick={() => onOpenChange(false)} className="px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 sm:py-1.5">Cancel</button>{onSecondary && secondaryLabel && (<button type="button" onClick={onSecondary} disabled={disabled} className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 sm:py-1.5">{secondaryLabel}</button>)}<button type="button" onClick={onConfirm} disabled={disabled} className={`inline-flex items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:py-1.5 ${confirmClassName}`}>{confirmLabel}</button></div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}




